import { CardDef, CardRules, FundDef, OfferDef, OfferItem } from "../data/offers";

/**
 * Cards and the growth fund (MONETISATION_PLAN MP-01/02/03; docs/SHOP.md §4.6): the rules of the `cards` save section.
 * Pure and deterministic, on whole UTC days (orders.ts utcDay); the caller hands in "today", already floored by the
 * newest signed store date (MP-13, Wallet.trustedNow), and the state never lets today run backwards (`seenDay`).
 *
 * A card is bought for real money (a Deals offer with `card`): it pays its items at once (the Deals grant), then its
 * daily items once per UTC day for `days` days from the UTC day of purchase, claimed in the hub. A day left unclaimed
 * can still be claimed for `bankDays` more days (offers.json cardRules), then it is gone. While a card runs it can be
 * bought again only in its last `rebuyWindowDays` days, and that purchase extends it. Each purchase is a segment of the
 * run; a refund (revocation) cancels its segment, so its unclaimed days are never paid.
 *
 * The fund (an offer with `fund`) pays each milestone once, when its level has been cleared: reached milestones are
 * claimed (at once when it is bought), never twice, whatever is bought or refunded later.
 */

/** Ledger and seed refs of what cards and funds pay: `card:<offerId>:<day>`, `fund:<offerId>:<level>`. */
export const CARD_REF = "card:";
export const FUND_REF = "fund:";

/** One purchase of a card: its transaction and the days it paid for. */
export interface CardSegment { readonly txn: string; readonly days: number; revoked: boolean }

/** A card's current run: from UTC day `start`, as long as its live segments' days; days through `claimedThrough` are done. */
export interface CardRun { start: number; segs: CardSegment[]; claimedThrough: number }

/** A fund bought (by `txn`; `revoked` once refunded) and the milestone levels already paid (kept across a re-buy). */
export interface FundRun { txn: string; revoked: boolean; claimed: number[] }

export interface CardsJson {
  cards: Record<string, CardRun>;
  funds: Record<string, FundRun>;
  seenDay: number;
  seen: string[];
}

/** An item `n` times over (a card's daily items for n days). */
export function scaleItem(it: OfferItem, n: number): OfferItem {
  switch (it.kind) {
    case "gems": return { ...it, amount: it.amount * n };
    case "supplies": return { ...it, chests: it.chests.mulInt(n) };
    case "skin": return it;
    default: return { ...it, count: it.count * n };
  }
}

export function scaleItems(items: readonly OfferItem[], n: number): OfferItem[] { return n > 0 ? items.map((i) => scaleItem(i, n)) : []; }

/**
 * Everything an offer pays over its life, which its honest value counts (meta/offerValue.ts): its items, a card's
 * daily items for all its days, a fund's every milestone.
 */
export function lifetimeItems(def: Pick<OfferDef, "items" | "card" | "fund">): OfferItem[] {
  return [...def.items, ...(def.card ? scaleItems(def.card.daily, def.card.days) : []), ...(def.fund ? def.fund.milestones.flatMap((m) => m.items) : [])];
}

const isInt = (n: unknown): n is number => Number.isInteger(n);
const isTxn = (t: unknown): t is string => typeof t === "string" && t !== "";

export class CardsState {
  cards: Record<string, CardRun> = {};
  funds: Record<string, FundRun> = {};
  /** The latest UTC day the card rules have seen: today never goes below it, so a clock moved back reopens nothing. */
  seenDay = -1;
  /** Things the player has looked at (the fund's one-time Deals badge). */
  seen: string[] = [];

  /** Today as the rules count it: the given UTC day, never before one already seen. */
  today(day: number): number { return Math.max(day, this.seenDay); }
  noteDay(day: number): void { if (isInt(day) && day > this.seenDay) this.seenDay = day; }

  hasSeen(key: string): boolean { return this.seen.includes(key); }
  markSeen(key: string): boolean { if (this.hasSeen(key)) return false; this.seen.push(key); return true; }

  // ------------------------------------------------------------------ cards

  run(id: string): CardRun | null { return this.cards[id] ?? null; }

  /** Days the run's live (not refunded) purchases paid for. */
  paidDays(id: string): number { return (this.run(id)?.segs ?? []).reduce((n, s) => n + (s.revoked ? 0 : s.days), 0); }

  /** The first UTC day after the run (exclusive end); null without a run. */
  endDay(id: string): number | null { const r = this.run(id); return r ? r.start + this.paidDays(id) : null; }

  /** Whether the card is running today (its days not over). */
  active(id: string, today: number): boolean { const e = this.endDay(id); return e !== null && today < e; }

  /** Today as the card's day number, 1-based ("Day 4 of 30"); 0 when it is not running. */
  dayNumber(id: string, today: number): number { const r = this.run(id); return r && this.active(id, today) ? today - r.start + 1 : 0; }

  /** Whether a purchase is allowed today: no run, the run over, or in its last `rebuyWindowDays` days (it then extends). */
  canBuy(id: string, rules: CardRules, today: number): boolean {
    const e = this.endDay(id);
    return e === null || e - today <= rules.rebuyWindowDays;
  }

  /** The days claimable today: not yet claimed, in the run, at most `bankDays` old. */
  claimable(id: string, rules: CardRules, today: number): number[] {
    const r = this.run(id), e = this.endDay(id);
    if (!r || e === null) return [];
    const from = Math.max(r.claimedThrough + 1, r.start, today - rules.bankDays), to = Math.min(today, e - 1);
    const out: number[] = [];
    for (let d = from; d <= to; d++) out.push(d);
    return out;
  }

  /** Claims today's claimable days (the caller pays each once); older unclaimed days beyond the bank are let go. */
  claim(id: string, rules: CardRules, today: number): number[] {
    const days = this.claimable(id, rules, today), r = this.run(id);
    if (!r || days.length === 0) return [];
    r.claimedThrough = days[days.length - 1];
    this.noteDay(today);
    return days;
  }

  /**
   * A verified purchase of a card on UTC day `day`: extends a run still going that day, else starts one from `day`
   * (the caller first pays any banked days of the old run). Once per transaction.
   */
  activate(id: string, def: CardDef, txn: string, day: number): "started" | "extended" | "duplicate" {
    if (this.payerSegment(txn)) return "duplicate";
    const r = this.run(id), e = this.endDay(id);
    if (r && e !== null && day < e) { r.segs.push({ txn, days: def.days, revoked: false }); return "extended"; }
    this.cards[id] = { start: day, segs: [{ txn, days: def.days, revoked: false }], claimedThrough: day - 1 };
    return "started";
  }

  /** The transaction that paid for `day` of a card (its live segments in purchase order); null outside the run. */
  payerOf(id: string, day: number): string | null {
    const r = this.run(id);
    if (!r) return null;
    let k = day - r.start;
    if (k < 0) return null;
    for (const s of r.segs) {
      if (s.revoked) continue;
      if (k < s.days) return s.txn;
      k -= s.days;
    }
    return null;
  }

  private payerSegment(txn: string): CardSegment | null {
    for (const r of Object.values(this.cards)) { const s = r.segs.find((x) => x.txn === txn); if (s) return s; }
    return null;
  }

  // ------------------------------------------------------------------ the fund

  fund(id: string): FundRun | null { return this.funds[id] ?? null; }
  /** Bought and not refunded. */
  ownsFund(id: string): boolean { const f = this.fund(id); return f !== null && !f.revoked; }

  /** A verified fund purchase: false (nothing changes) when it is owned already. A re-buy after a refund keeps the milestones paid. */
  buyFund(id: string, txn: string): boolean {
    const f = this.fund(id);
    if (f && (!f.revoked || f.txn === txn)) return false;
    this.funds[id] = { txn, revoked: false, claimed: f ? [...f.claimed] : [] };
    return true;
  }

  /** Milestone levels reached (cleared) and not yet paid, while the fund is owned. */
  fundOwed(id: string, def: FundDef, cleared: number): number[] {
    const f = this.fund(id);
    if (!f || f.revoked) return [];
    return def.milestones.filter((m) => m.level <= cleared && !f.claimed.includes(m.level)).map((m) => m.level);
  }

  /** Marks one milestone paid (the caller pays it once, ref `fund:<id>:<level>`). */
  claimFund(id: string, def: FundDef, level: number, cleared: number): readonly OfferItem[] | string {
    const f = this.fund(id), m = def.milestones.find((x) => x.level === level);
    if (!m) return "no such milestone";
    if (!f || f.revoked) return "not owned";
    if (f.claimed.includes(level)) return "already claimed";
    if (cleared < level) return "not reached";
    f.claimed.push(level);
    return m.items;
  }

  // ------------------------------------------------------------------ refunds

  /** A refund: cancels the transaction's card segment or fund, so nothing more is paid for it. Returns whether it held one. */
  revoke(txn: string): boolean {
    const s = this.payerSegment(txn);
    if (s) { if (s.revoked) return false; s.revoked = true; return true; }
    for (const f of Object.values(this.funds)) if (f.txn === txn && !f.revoked) { f.revoked = true; return true; }
    return false;
  }

  toJson(): CardsJson {
    const cards: Record<string, CardRun> = {}, funds: Record<string, FundRun> = {};
    for (const [k, r] of Object.entries(this.cards)) cards[k] = { start: r.start, segs: r.segs.map((s) => ({ ...s })), claimedThrough: r.claimedThrough };
    for (const [k, f] of Object.entries(this.funds)) funds[k] = { ...f, claimed: [...f.claimed] };
    return { cards, funds, seenDay: this.seenDay, seen: [...this.seen] };
  }

  /** Lenient restore, field by field: the section holds paid days, so one bad entry never costs the rest. */
  static fromJson(j: unknown): CardsState {
    const s = new CardsState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<CardsJson>;
    for (const [k, r] of Object.entries(src.cards ?? {})) {
      if (!r || !isInt(r.start) || !isInt(r.claimedThrough) || !Array.isArray(r.segs)) continue;
      const segs = r.segs.filter((x) => x && isTxn(x.txn) && isInt(x.days) && x.days > 0).map((x) => ({ txn: x.txn, days: x.days, revoked: x.revoked === true }));
      if (segs.length > 0) s.cards[k] = { start: r.start, segs, claimedThrough: r.claimedThrough };
    }
    for (const [k, f] of Object.entries(src.funds ?? {})) {
      if (f && isTxn(f.txn)) s.funds[k] = { txn: f.txn, revoked: f.revoked === true, claimed: Array.isArray(f.claimed) ? f.claimed.filter(isInt) : [] };
    }
    if (isInt(src.seenDay)) s.seenDay = src.seenDay;
    if (Array.isArray(src.seen)) s.seen = src.seen.filter((x): x is string => typeof x === "string");
    return s;
  }
}
